/**
 * Inline SVG icons (Lucide-based, MIT license).
 *
 * Решение F1+F7 (план stateless-whistling-grove.md, 2026-05-10):
 * Системные эмодзи (Apple/Windows) рендерили жёлтым цветом — нарушение
 * teal-палитры brand-guidelines («никакого жёлтого нигде»).
 *
 * Заменены:
 * - 📁 (folder) → iconFolder() — слоты документов в модалке «Новый проект»
 * - 🔒 / 🔓 (lock) → iconLock(open=false) — замочек baseline в Gantt-toolbar
 *
 * Цвет управляется через CSS `currentColor` (родительский элемент задаёт
 * `color: var(--teal-bright)` или `color: var(--text-secondary)` etc).
 *
 * Источник SVG path: lucide.dev (MIT). Inline для нулевой зависимости
 * (vanilla JS проект, нет npm-импорта).
 */

const SIZE_DEFAULT = 16;

function svg(path, size = SIZE_DEFAULT, extraAttrs = '') {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" `
    + `viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" `
    + `stroke-linecap="round" stroke-linejoin="round" ${extraAttrs}>${path}</svg>`;
}

/** Folder (lucide-folder). Цвет — currentColor. */
export function iconFolder(size = SIZE_DEFAULT) {
  return svg(
    '<path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>',
    size,
  );
}

/** Lock — закрытый замок (lucide-lock). */
export function iconLockClosed(size = SIZE_DEFAULT) {
  return svg(
    '<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>'
    + '<path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
    size,
  );
}

/** Lock — открытый замок (lucide-lock-open). */
export function iconLockOpen(size = SIZE_DEFAULT) {
  return svg(
    '<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>'
    + '<path d="M7 11V7a5 5 0 0 1 9.9-1"/>',
    size,
  );
}

/** Универсальный замок — выбор по флагу. */
export function iconLock(open = false, size = SIZE_DEFAULT) {
  return open ? iconLockOpen(size) : iconLockClosed(size);
}

/** Refresh-cw (lucide-refresh-cw) — для кнопок «Пересчитать». */
export function iconRefresh(size = SIZE_DEFAULT) {
  return svg(
    '<path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>'
    + '<path d="M3 3v5h5"/>'
    + '<path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>'
    + '<path d="M16 16h5v5"/>',
    size,
  );
}

/** Undo-2 (lucide-undo-2) — «Привести к базовому» (baseline → current). */
export function iconUndo(size = SIZE_DEFAULT) {
  return svg(
    '<path d="M9 14 4 9l5-5"/>'
    + '<path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11"/>',
    size,
  );
}

/* ── ERP shell (UI-1, план 2026-09-25): сайдбар и бургер в шапке ── */

/** Chevron вправо (lucide-chevron-right); в открытом модуле поворачивается CSS-ом. */
export function iconChevronRight(size = SIZE_DEFAULT) {
  return svg('<path d="m9 18 6-6-6-6"/>', size);
}

/** Бургер (lucide-menu) — открывает сайдбар на ширине < 900px. */
export function iconMenu(size = SIZE_DEFAULT) {
  return svg('<path d="M4 6h16"/><path d="M4 12h16"/><path d="M4 18h16"/>', size);
}

/** Панель слева: close=true — «Свернуть» (стрелка внутрь), false — «Развернуть». */
export function iconPanelLeft(close = true, size = SIZE_DEFAULT) {
  return svg(
    '<rect width="18" height="18" x="3" y="3" rx="2"/><path d="M9 3v18"/>'
    + (close ? '<path d="m16 15-3-3 3-3"/>' : '<path d="m14 9 3 3-3 3"/>'),
    size,
  );
}

const MODULE_ICON_PATHS = {
  // lucide-layout-dashboard
  desk: '<rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/>'
    + '<rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/>',
  // lucide-briefcase
  sales: '<path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><rect width="20" height="14" x="2" y="6" rx="2"/>',
  // lucide-layers
  projects: '<path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0'
    + 'l8.58-3.9a1 1 0 0 0 0-1.83Z"/><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/>'
    + '<path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/>',
  // lucide-gantt-chart
  ccpm: '<path d="M8 6h10"/><path d="M6 12h9"/><path d="M11 18h7"/>',
  // lucide-users
  people: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>'
    + '<path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  // lucide-wallet
  finance: '<path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3'
    + 'a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"/><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"/>',
  // lucide-bar-chart-3
  director: '<path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>',
  // lucide-settings (упрощённый контур)
  admin: '<circle cx="12" cy="12" r="3"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/>'
    + '<path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/>'
    + '<path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/>',
};

/** Иконка модуля сайдбара по ключу из utils/erp-modules.js. */
export function iconModule(key, size = SIZE_DEFAULT) {
  return svg(MODULE_ICON_PATHS[key] ?? MODULE_ICON_PATHS.desk, size);
}
